/**
 * Rules-management table (#118).
 *
 * Renders a single scope's rules (generic or customer) - the parent route
 * owns the tab + customer-filter UI. Each row carries a click target that
 * opens the edit dialog with the live rule shape; the dialog handles the
 * mutation + cache invalidation.
 *
 * The active-toggle in the actions column is intentionally a separate
 * button (instead of an inline checkbox) so a single tap can flip the rule
 * without opening the editor; this matches the architect spec on #118.
 */
import type { RuleListItem } from '@invoice/shared/schemas';
import { CheckCircle2, FilePenLine, FlaskConical, ScrollText, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

interface RulesTableProps {
  items: RuleListItem[];
  isLoading: boolean;
  onEdit: (rule: RuleListItem) => void;
  onTestRun: (rule: RuleListItem) => void;
  onToggleActive: (rule: RuleListItem) => void;
  onDelete: (rule: RuleListItem) => void;
  testRunPendingId: string | null;
  togglePendingId: string | null;
  deletePendingId: string | null;
}

const COLUMN_COUNT = 7;
const SKELETON_ROW_COUNT = 3;

export function RulesTable({
  items,
  isLoading,
  onEdit,
  onTestRun,
  onToggleActive,
  onDelete,
  testRunPendingId,
  togglePendingId,
  deletePendingId,
}: RulesTableProps) {
  const { formatDateTime, t } = useI18n();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('rulesTable.title')}</TableHead>
          <TableHead>{t('rulesTable.tenant')}</TableHead>
          <TableHead>{t('rulesTable.status')}</TableHead>
          <TableHead>{t('rulesTable.version')}</TableHead>
          <TableHead>{t('rulesTable.lastEditor')}</TableHead>
          <TableHead>{t('rulesTable.updated')}</TableHead>
          <TableHead className="text-right">{t('rulesTable.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <EmptyRow />
        ) : (
          items.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              formatDateTime={formatDateTime}
              onEdit={onEdit}
              onTestRun={onTestRun}
              onToggleActive={onToggleActive}
              onDelete={onDelete}
              testRunPending={testRunPendingId === rule.id}
              togglePending={togglePendingId === rule.id}
              deletePending={deletePendingId === rule.id}
            />
          ))
        )}
      </TableBody>
    </Table>
  );
}

interface RuleRowProps {
  rule: RuleListItem;
  formatDateTime: (value: string) => string;
  onEdit: (rule: RuleListItem) => void;
  onTestRun: (rule: RuleListItem) => void;
  onToggleActive: (rule: RuleListItem) => void;
  onDelete: (rule: RuleListItem) => void;
  testRunPending: boolean;
  togglePending: boolean;
  deletePending: boolean;
}

function RuleRow({
  rule,
  formatDateTime,
  onEdit,
  onTestRun,
  onToggleActive,
  onDelete,
  testRunPending,
  togglePending,
  deletePending,
}: RuleRowProps) {
  const { t } = useI18n();

  return (
    <TableRow data-testid={`rule-row-${rule.id}`}>
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">{rule.title}</span>
          {rule.scope === 'customer' && rule.supplierName ? (
            <span className="text-xs text-muted-foreground">Lieferant: {rule.supplierName}</span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {rule.customerName ?? '—'}
      </TableCell>
      <TableCell>
        {rule.active ? (
          <Badge variant="success">
            <CheckCircle2 className="size-3" aria-hidden />
            {t('rulesTable.active')}
          </Badge>
        ) : (
          <Badge variant="muted">{t('rulesTable.inactive')}</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">v{rule.version}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{rule.lastEditorEmail}</TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {formatDateTime(rule.lastEditedAt)}
      </TableCell>
      <TableCell className="text-right">
        <div className="inline-flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onToggleActive(rule)}
            disabled={togglePending}
            aria-label={
              rule.active ? t('rulesTable.toggleInactiveAria') : t('rulesTable.toggleActiveAria')
            }
            data-testid={`rule-row-toggle-${rule.id}`}
          >
            {rule.active ? t('rulesTable.deactivate') : t('rulesTable.activate')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onEdit(rule)}
            aria-label={t('rulesTable.editAria', { title: rule.title })}
            data-testid={`rule-row-edit-${rule.id}`}
          >
            <FilePenLine className="size-3.5" aria-hidden />
            {t('rulesTable.edit')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onTestRun(rule)}
            disabled={testRunPending}
            aria-label={t('rulesTable.testRunAria', { title: rule.title })}
            data-testid={`rule-row-test-run-${rule.id}`}
          >
            <FlaskConical className="size-3.5" aria-hidden />
            {t('rulesTable.testRun')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => onDelete(rule)}
            disabled={deletePending}
            aria-label={t('rulesTable.deleteAria', { title: rule.title })}
            data-testid={`rule-row-delete-${rule.id}`}
          >
            <Trash2 className="size-3.5" aria-hidden />
            {t('rulesTable.delete')}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, idx) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are decorative placeholders, never reordered.
        <TableRow key={`rule-skeleton-${idx}`} data-testid="rule-skeleton-row">
          {Array.from({ length: COLUMN_COUNT }, (_, colIdx) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: same as above.
            <TableCell key={`rule-skeleton-${idx}-${colIdx}`}>
              <div
                className={cn(
                  'h-4 w-full animate-pulse rounded bg-muted',
                  colIdx === COLUMN_COUNT - 1 && 'ml-auto w-24',
                )}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyRow() {
  const { t } = useI18n();

  return (
    <TableRow data-testid="rule-empty-row">
      <TableCell colSpan={COLUMN_COUNT} className="py-12">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <ScrollText className="size-8 text-muted-foreground" aria-hidden />
          <p className="text-sm font-medium text-foreground">{t('rulesTable.emptyTitle')}</p>
          <p className="max-w-sm text-xs text-muted-foreground">
            {t('rulesTable.emptyDescription')}
          </p>
        </div>
      </TableCell>
    </TableRow>
  );
}
